import { Box } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { colorTokens, getPriorityColor, getStatusColor, radiusTokens, typographyTokens } from '../app/designTokens';
import {
  getTenantAccessStatusLabel,
  getOnboardingIntentStatusLabel,
  getPlanStatusLabel,
  getProviderLabel,
  getSubscriptionStatusLabel,
  getSupportPriorityLabel,
  getSupportStatusLabel,
  getPaymentStatusLabel,
  getInvoiceStatusLabel,
  getSetupSessionStatusLabel,
  getSetupStepStatusLabel,
  getProvisioningRunStatusLabel,
  getProvisioningStepStatusLabel,
  getHealthCheckStatusLabel,
  getProvisioningStatusLabel,
  getSchemaVersionStatusLabel,
  getSchemaPlanStatusLabel,
  getSchemaRiskLabel,
  getTenantStatusLabel,
  getBillingReadinessLabel,
  getMembershipStatusLabel,
  getInvitationStatusLabel,
  getCustomerStatusLabel,
} from '../utils/statusLabels';

const { semantic } = colorTokens;

const SIZE_STYLES = {
  Sm: { height: 24, fontSize: 12, px: '10px' },
  sm: { height: 24, fontSize: 12, px: '10px' },
  Md: { height: 28, fontSize: 13, px: '10px' },
  md: { height: 28, fontSize: 13, px: '10px' },
};

/** Figma Feedback / StatusBadge — bordered dark badge (not filled MUI Chip). */
const TONE_STYLES = {
  Neutral: {
    bgcolor: semantic.status.neutral.bg,
    color: semantic.status.neutral.text,
    borderColor: semantic.status.neutral.border,
  },
  Info: {
    bgcolor: alpha(semantic.info, 0.12),
    color: '#7ec8e3',
    borderColor: alpha(semantic.info, 0.55),
  },
  Success: {
    bgcolor: alpha(semantic.success, 0.12),
    color: '#8bc48a',
    borderColor: alpha(semantic.success, 0.55),
  },
  Warning: {
    bgcolor: alpha(semantic.warning, 0.12),
    color: '#f0b57a',
    borderColor: alpha(semantic.warning, 0.55),
  },
  Danger: {
    bgcolor: alpha(semantic.error, 0.12),
    color: '#f0a0a0',
    borderColor: alpha(semantic.error, 0.55),
  },
  Brand: {
    bgcolor: semantic.status.brand.bg,
    color: semantic.status.brand.text,
    borderColor: semantic.status.brand.border,
  },
};

const MUI_COLOR_TO_TONE = {
  default: 'Neutral',
  primary: 'Brand',
  secondary: 'Neutral',
  success: 'Success',
  warning: 'Warning',
  error: 'Danger',
  info: 'Info',
};

function resolveLabel(status, category, apiLabel) {
  switch (category) {
    case 'subscription':
      return getSubscriptionStatusLabel(status, apiLabel);
    case 'payment':
      return getPaymentStatusLabel(status, apiLabel);
    case 'invoice':
      return getInvoiceStatusLabel(status, apiLabel);
    case 'support':
      return getSupportStatusLabel(status, apiLabel);
    case 'priority':
      return getSupportPriorityLabel(status, apiLabel);
    case 'tenant_access':
      return getTenantAccessStatusLabel(status, apiLabel);
    case 'onboarding_intent':
      return getOnboardingIntentStatusLabel(status, apiLabel);
    case 'setup_session':
      return getSetupSessionStatusLabel(status, apiLabel);
    case 'setup_step':
      return getSetupStepStatusLabel(status, apiLabel);
    case 'provisioning_run':
      return getProvisioningRunStatusLabel(status, apiLabel);
    case 'provisioning_step':
      return getProvisioningStepStatusLabel(status, apiLabel);
    case 'health_check':
      return getHealthCheckStatusLabel(status, apiLabel);
    case 'provisioning_status':
      return getProvisioningStatusLabel(status, apiLabel);
    case 'schema_version':
      return getSchemaVersionStatusLabel(status, apiLabel);
    case 'schema_plan':
      return getSchemaPlanStatusLabel(status, apiLabel);
    case 'schema_risk':
      return getSchemaRiskLabel(status, apiLabel);
    case 'plan':
      return getPlanStatusLabel(status, apiLabel);
    case 'provider':
      return getProviderLabel(status, apiLabel);
    case 'tenant':
      return getTenantStatusLabel(status, apiLabel);
    case 'customer':
      return getCustomerStatusLabel(status, apiLabel);
    case 'membership':
      return getMembershipStatusLabel(status, apiLabel);
    case 'invitation':
      return getInvitationStatusLabel(status, apiLabel);
    case 'billing':
    case 'billing_readiness':
      return getBillingReadinessLabel(status, apiLabel);
    case 'generic':
      return apiLabel || status;
    default:
      return getTenantStatusLabel(status, apiLabel) || apiLabel || status;
  }
}

function resolveTone({ tone, status, category }) {
  if (tone && TONE_STYLES[tone]) {
    return tone;
  }

  const mappedColor = category === 'priority'
    ? getPriorityColor(status)
    : getStatusColor(status, category);

  return MUI_COLOR_TO_TONE[mappedColor] || 'Neutral';
}

/**
 * Unified status/priority badge (Feedback / StatusBadge).
 * Preserves existing status/category/apiLabel API; adds optional size + tone.
 *
 * @param {string} status - Raw status or priority value from API
 * @param {string} [category='auto']
 * @param {string} [apiLabel]
 * @param {'Sm'|'Md'|'sm'|'md'} [size='Sm']
 * @param {'Neutral'|'Info'|'Success'|'Warning'|'Danger'|'Brand'} [tone]
 */
export function StatusBadge({
  status,
  category = 'auto',
  apiLabel,
  size = 'Sm',
  tone,
  sx,
  ...rest
}) {
  if (!status) {
    return null;
  }

  const label = resolveLabel(status, category, apiLabel);
  const resolvedTone = resolveTone({ tone, status, category });
  const sizeSx = SIZE_STYLES[size] || SIZE_STYLES.Sm;
  const toneSx = TONE_STYLES[resolvedTone] || TONE_STYLES.Neutral;

  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxSizing: 'border-box',
        height: sizeSx.height,
        minHeight: sizeSx.height,
        maxHeight: sizeSx.height,
        px: sizeSx.px,
        borderRadius: `${radiusTokens.pill}px`,
        border: '1px solid',
        borderColor: toneSx.borderColor,
        bgcolor: toneSx.bgcolor,
        color: toneSx.color,
        fontFamily: typographyTokens.fontFamily,
        fontWeight: typographyTokens.fontWeightSemibold,
        fontSize: sizeSx.fontSize,
        lineHeight: 1,
        letterSpacing: '0.02em',
        whiteSpace: 'nowrap',
        verticalAlign: 'middle',
        alignSelf: 'center',
        flexShrink: 0,
        ...sx,
      }}
      {...rest}
    >
      {label}
    </Box>
  );
}
